import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { LuSparkles, LuInfo } from 'react-icons/lu'
import { H6 } from '@/components/ui/Headings'
import { Tooltip } from '@/components/ui/Tooltip'

import type { Design } from '@/api/designs'

interface DesignMetadataPanelProps {
  design: Design
}

function toSentenceCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export default function DesignMetadataPanel({
  design
}: DesignMetadataPanelProps) {
  const description = design.originalPrompt || design.augmentedPrompt
  const colorSwatches = design.paletteColors?.slice(0, 8) ?? []
  const visualStyle = design.visualStyle?.trim() || null
  const wildness = design.creativityLevel
    ? toSentenceCase(design.creativityLevel)
    : null
  const sellingScore = design.sellingScore != null ? design.sellingScore : null

  const aspectRatio = design.aspectRatio?.trim() || null

  const hasMetadata =
    description ||
    colorSwatches.length ||
    aspectRatio ||
    visualStyle ||
    wildness ||
    design.subjectImageUrls?.length

  if (!hasMetadata) return null

  return (
    <Flex flexDirection="column" gap="12px" w="100%">
      <H6 color="whiteAlpha.900" mb="1px">
        {design.name}
      </H6>

      {sellingScore != null && (
        <Box
          position="relative"
          overflow="hidden"
          px="12px"
          py="10px"
          borderRadius="12px"
          bg="rgba(0, 112, 243, 0.06)"
          border="1px solid"
          borderColor="rgba(0, 112, 243, 0.14)"
          css={{
            animation: 'borderGlow 3s ease-in-out infinite'
          }}
        >
          <Box
            position="absolute"
            inset={0}
            borderRadius="12px"
            className="animate-shimmer"
            pointerEvents="none"
          />

          <Flex align="center" justify="space-between" position="relative">
            <Flex align="center" gap="6px">
              <Box color="accent.400" display="flex">
                <LuSparkles size={13} />
              </Box>
              <Text textStyle="label" color="accent.200" letterSpacing="0.08em">
                Selling Score
              </Text>
            </Flex>

            <Flex align="center" gap="6px">
              <Text
                textStyle="subtitle"
                fontWeight="600"
                letterSpacing="-0.02em"
                bgGradient="to-r"
                gradientFrom="white"
                gradientTo="accent.200"
                bgClip="text"
              >
                {sellingScore}%
              </Text>
              <Tooltip
                showArrow
                content="Our system analyzed current market trends and competition to estimate how likely this design is to sell."
                contentProps={{
                  maxW: '220px',
                  bg: 'bg.tertiary',
                  color: 'text.primary',
                  px: '10px',
                  py: '8px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'border.DEFAULT',
                  fontSize: '12px'
                }}
              >
                <Box
                  color="accent.300"
                  display="flex"
                  cursor="pointer"
                  opacity={0.7}
                  transition="opacity 0.2s ease"
                  _hover={{ opacity: 1 }}
                >
                  <LuInfo size={13} />
                </Box>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
      )}

      {!!colorSwatches.length && (
        <Box pt="2px">
          <Text textStyle="caption" color="whiteAlpha.500" mb="6px">
            Color palette
          </Text>
          <Flex gap="6px" flexWrap="wrap">
            {colorSwatches.map((color) => (
              <Box
                key={`${color.hex}-${color.name}`}
                w="16px"
                h="16px"
                borderRadius="full"
                bg={color.hex}
                border="1px solid"
                borderColor="rgba(255,255,255,0.25)"
              />
            ))}
          </Flex>
        </Box>
      )}

      {aspectRatio && (
        <Box pt="2px">
          <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
            Aspect ratio
          </Text>
          <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
            {aspectRatio}
          </Text>
        </Box>
      )}

      {wildness && (
        <Box pt="2px">
          <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
            Creativity
          </Text>
          <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
            {wildness}
          </Text>
        </Box>
      )}

      {visualStyle && (
        <Box pt="2px">
          <Text textStyle="caption" color="whiteAlpha.500" mb="2px">
            Visual style
          </Text>
          <Text textStyle="bodySuperSmall" color="whiteAlpha.900">
            {visualStyle}
          </Text>
        </Box>
      )}

      {description && (
        <Box
          mt="2px"
          px="10px"
          py="9px"
          borderRadius="10px"
          bg="rgba(255,255,255,0.04)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
        >
          <Text textStyle="caption" color="whiteAlpha.700">
            {description}
          </Text>
        </Box>
      )}

      {!!design.subjectImageUrls?.length && (
        <Box>
          <Text textStyle="caption" color="whiteAlpha.500" mb="6px">
            Subject Images
          </Text>
          <Flex gap="6px" flexWrap="wrap">
            {design.subjectImageUrls.map((url, i) => (
              <Image
                key={i}
                src={url}
                alt={`Subject ${i + 1}`}
                w="32px"
                h="32px"
                objectFit="cover"
                borderRadius="8px"
                border="1px solid"
                borderColor="rgba(255,255,255,0.16)"
              />
            ))}
          </Flex>
        </Box>
      )}

    </Flex>
  )
}
